import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check, ChevronDown, TrendingUp, Shield, Percent, Calendar, IndianRupee, Search } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

// ── FD Interest Rate Data (verified March 2026 from official bank websites) ───
// Format: { regular: { tenures... }, senior: { tenures... } }
// Tenures in months: 7, 12, 15, 18, 24, 36, 60
const FD_BANKS = [
  {
    id: 'sbi',
    name: 'State Bank of India',
    shortName: 'SBI',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.25, 15: 6.25, 18: 6.25, 24: 6.45, 36: 6.30, 60: 6.05 },
      senior:  { 7: 5.00, 12: 6.75, 15: 6.75, 18: 6.75, 24: 6.95, 36: 6.80, 60: 7.05 },
    },
  },
  {
    id: 'hdfc',
    name: 'HDFC Bank',
    shortName: 'HDFC',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.60, 15: 7.05, 18: 7.05, 24: 6.70, 36: 6.75, 60: 6.50 },
      senior:  { 7: 5.00, 12: 7.10, 15: 7.55, 18: 7.55, 24: 7.20, 36: 7.25, 60: 7.00 },
    },
  },
  {
    id: 'icici',
    name: 'ICICI Bank',
    shortName: 'ICICI',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.75, 12: 6.70, 15: 7.25, 18: 7.25, 24: 7.00, 36: 7.00, 60: 6.90 },
      senior:  { 7: 5.25, 12: 7.20, 15: 7.85, 18: 7.75, 24: 7.50, 36: 7.50, 60: 7.40 },
    },
  },
  {
    id: 'axis',
    name: 'Axis Bank',
    shortName: 'Axis',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.75, 12: 6.70, 15: 7.25, 18: 7.25, 24: 7.10, 36: 7.10, 60: 7.00 },
      senior:  { 7: 5.25, 12: 7.20, 15: 7.75, 18: 7.75, 24: 7.60, 36: 7.60, 60: 7.75 },
    },
  },
  {
    id: 'kotak',
    name: 'Kotak Mahindra Bank',
    shortName: 'Kotak',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 7.10, 15: 7.25, 18: 7.30, 24: 7.15, 36: 7.00, 60: 6.20 },
      senior:  { 7: 5.00, 12: 7.60, 15: 7.75, 18: 7.80, 24: 7.65, 36: 7.50, 60: 6.70 },
    },
  },
  {
    id: 'pnb',
    name: 'Punjab National Bank',
    shortName: 'PNB',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.25, 15: 6.40, 18: 6.40, 24: 6.40, 36: 6.25, 60: 6.25 },
      senior:  { 7: 5.00, 12: 6.75, 15: 6.90, 18: 6.90, 24: 6.90, 36: 6.75, 60: 6.75 },
    },
  },
  {
    id: 'bob',
    name: 'Bank of Baroda',
    shortName: 'BoB',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.85, 15: 7.00, 18: 7.00, 24: 7.00, 36: 7.15, 60: 6.80 },
      senior:  { 7: 5.00, 12: 7.35, 15: 7.50, 18: 7.50, 24: 7.50, 36: 7.65, 60: 7.40 },
    },
  },
  {
    id: 'canara',
    name: 'Canara Bank',
    shortName: 'Canara',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.85, 15: 7.05, 18: 7.05, 24: 7.15, 36: 7.20, 60: 6.70 },
      senior:  { 7: 4.00, 12: 7.35, 15: 7.55, 18: 7.55, 24: 7.65, 36: 7.70, 60: 7.20 },
    },
  },
  {
    id: 'iob',
    name: 'Indian Overseas Bank',
    shortName: 'IOB',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.50, 15: 6.50, 18: 6.50, 24: 6.50, 36: 6.50, 60: 6.50 },
      senior:  { 7: 5.00, 12: 7.00, 15: 7.00, 18: 7.00, 24: 7.00, 36: 7.00, 60: 7.00 },
    },
  },
  {
    id: 'union',
    name: 'Union Bank of India',
    shortName: 'Union Bank',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 3.50, 12: 6.25, 15: 6.20, 18: 6.20, 24: 6.00, 36: 5.90, 60: 5.90 },
      senior:  { 7: 4.00, 12: 6.75, 15: 6.70, 18: 6.70, 24: 6.50, 36: 6.40, 60: 6.40 },
    },
  },
  {
    id: 'idbi',
    name: 'IDBI Bank',
    shortName: 'IDBI',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.55, 12: 6.80, 15: 6.80, 18: 6.80, 24: 6.80, 36: 6.50, 60: 6.50 },
      senior:  { 7: 5.05, 12: 7.30, 15: 7.30, 18: 7.30, 24: 7.30, 36: 7.00, 60: 7.00 },
    },
  },
  {
    id: 'indusind',
    name: 'IndusInd Bank',
    shortName: 'IndusInd',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.75, 15: 7.00, 18: 7.00, 24: 6.90, 36: 6.65, 60: 6.50 },
      senior:  { 7: 4.50, 12: 7.25, 15: 7.50, 18: 7.50, 24: 7.40, 36: 7.15, 60: 7.00 },
    },
  },
  {
    id: 'idfcfirst',
    name: 'IDFC FIRST Bank',
    shortName: 'IDFC FIRST',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.50, 15: 7.00, 18: 7.25, 24: 7.00, 36: 6.50, 60: 6.25 },
      senior:  { 7: 5.00, 12: 7.00, 15: 7.50, 18: 7.75, 24: 7.50, 36: 7.00, 60: 6.75 },
    },
  },
  {
    id: 'yes',
    name: 'Yes Bank',
    shortName: 'Yes Bank',
    type: 'Private Bank',
    rates: {
      regular: { 7: 5.00, 12: 7.00, 15: 7.50, 18: 7.50, 24: 7.50, 36: 7.50, 60: 7.50 },
      senior:  { 7: 5.50, 12: 7.50, 15: 8.00, 18: 8.00, 24: 8.00, 36: 8.25, 60: 8.25 },
    },
  },
  {
    id: 'rbl',
    name: 'RBL Bank',
    shortName: 'RBL',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.75, 12: 7.00, 15: 7.00, 18: 7.00, 24: 7.00, 36: 7.20, 60: 7.00 },
      senior:  { 7: 5.25, 12: 7.50, 15: 7.50, 18: 7.50, 24: 7.50, 36: 7.70, 60: 7.50 },
    },
  },
  {
    id: 'dcb',
    name: 'DCB Bank',
    shortName: 'DCB',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.75, 12: 7.10, 15: 7.15, 18: 7.10, 24: 7.40, 36: 7.55, 60: 7.40 },
      senior:  { 7: 5.25, 12: 7.60, 15: 7.65, 18: 7.60, 24: 7.90, 36: 8.05, 60: 7.90 },
    },
  },
  {
    id: 'ujjivan',
    name: 'Ujjivan Small Finance Bank',
    shortName: 'Ujjivan SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.00, 12: 8.25, 15: 8.00, 18: 8.00, 24: 8.00, 36: 7.20, 60: 7.20 },
      senior:  { 7: 4.50, 12: 8.75, 15: 8.50, 18: 8.50, 24: 8.50, 36: 7.70, 60: 7.70 },
    },
  },
  {
    id: 'equitas',
    name: 'Equitas Small Finance Bank',
    shortName: 'Equitas SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.00, 12: 8.20, 15: 8.20, 18: 8.20, 24: 8.20, 36: 7.50, 60: 7.25 },
      senior:  { 7: 4.50, 12: 8.70, 15: 8.70, 18: 8.70, 24: 8.70, 36: 8.00, 60: 7.75 },
    },
  },
  {
    id: 'suryoday',
    name: 'Suryoday Small Finance Bank',
    shortName: 'Suryoday SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.85, 15: 8.25, 18: 8.50, 24: 8.60, 36: 8.60, 60: 8.25 },
      senior:  { 7: 4.50, 12: 7.35, 15: 8.75, 18: 9.00, 24: 9.10, 36: 9.10, 60: 8.75 },
    },
  },
  {
    id: 'jana',
    name: 'Jana Small Finance Bank',
    shortName: 'Jana SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.25, 12: 8.50, 15: 8.25, 18: 8.25, 24: 8.25, 36: 7.25, 60: 7.25 },
      senior:  { 7: 4.75, 12: 9.00, 15: 8.75, 18: 8.75, 24: 8.75, 36: 7.75, 60: 7.75 },
    },
  },
  {
    id: 'shriram',
    name: 'Shriram Finance',
    shortName: 'Shriram',
    type: 'NBFC',
    rates: {
      regular: { 7: 0, 12: 7.00, 15: 7.00, 18: 7.15, 24: 7.25, 36: 7.60, 60: 7.60 },
      senior:  { 7: 0, 12: 7.50, 15: 7.50, 18: 7.65, 24: 7.75, 36: 8.10, 60: 8.10 },
    },
  },
  {
    id: 'bajaj',
    name: 'Bajaj Finance',
    shortName: 'Bajaj Finance',
    type: 'NBFC',
    rates: {
      regular: { 7: 0, 12: 6.60, 15: 6.75, 18: 6.75, 24: 6.95, 36: 6.95, 60: 6.95 },
      senior:  { 7: 0, 12: 6.95, 15: 7.10, 18: 7.10, 24: 7.30, 36: 7.30, 60: 7.30 },
    },
  },
  {
    id: 'mahindra',
    name: 'Mahindra Finance',
    shortName: 'Mahindra Fin',
    type: 'NBFC',
    rates: {
      regular: { 7: 0, 12: 6.60, 15: 7.00, 18: 7.00, 24: 7.00, 36: 7.00, 60: 7.00 },
      senior:  { 7: 0, 12: 6.85, 15: 7.25, 18: 7.25, 24: 7.25, 36: 7.25, 60: 7.25 },
    },
  },
  {
    id: 'pfc',
    name: 'Power Finance Corporation',
    shortName: 'PFC',
    type: 'NBFC (Govt)',
    rates: {
      regular: { 7: 0, 12: 7.25, 15: 7.35, 18: 7.35, 24: 7.35, 36: 7.50, 60: 7.55 },
      senior:  { 7: 0, 12: 7.50, 15: 7.60, 18: 7.60, 24: 7.60, 36: 7.75, 60: 7.80 },
    },
  },
  {
    id: 'boi',
    name: 'Bank of India',
    shortName: 'BOI',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.25, 15: 6.30, 18: 6.30, 24: 6.30, 36: 6.25, 60: 6.00 },
      senior:  { 7: 5.00, 12: 6.75, 15: 6.80, 18: 6.80, 24: 6.80, 36: 7.00, 60: 6.75 },
    },
  },
  {
    id: 'centralbank',
    name: 'Central Bank of India',
    shortName: 'Central Bank',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.20, 15: 6.30, 18: 6.30, 24: 6.00, 36: 6.00, 60: 6.00 },
      senior:  { 7: 5.00, 12: 6.70, 15: 6.80, 18: 6.80, 24: 6.50, 36: 6.50, 60: 6.50 },
    },
  },
  {
    id: 'indianbank',
    name: 'Indian Bank',
    shortName: 'Indian Bank',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.25, 12: 6.80, 15: 7.05, 18: 7.05, 24: 6.80, 36: 6.75, 60: 6.50 },
      senior:  { 7: 4.75, 12: 7.30, 15: 7.55, 18: 7.55, 24: 7.30, 36: 7.25, 60: 7.00 },
    },
  },
  {
    id: 'uco',
    name: 'UCO Bank',
    shortName: 'UCO',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 3.50, 12: 6.10, 15: 6.10, 18: 6.10, 24: 6.00, 36: 6.00, 60: 6.00 },
      senior:  { 7: 4.00, 12: 6.35, 15: 6.60, 18: 6.60, 24: 6.50, 36: 6.50, 60: 6.50 },
    },
  },
  {
    id: 'bom',
    name: 'Bank of Maharashtra',
    shortName: 'Bank of Maha',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.25, 12: 6.20, 15: 6.20, 18: 6.15, 24: 5.25, 36: 5.00, 60: 5.00 },
      senior:  { 7: 4.75, 12: 6.70, 15: 6.70, 18: 6.65, 24: 5.75, 36: 5.50, 60: 5.50 },
    },
  },
  {
    id: 'punjabandsind',
    name: 'Punjab & Sind Bank',
    shortName: 'P&S Bank',
    type: 'PSU Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.80, 15: 7.00, 18: 7.00, 24: 6.80, 36: 6.50, 60: 6.25 },
      senior:  { 7: 4.50, 12: 7.30, 15: 7.50, 18: 7.50, 24: 7.30, 36: 7.00, 60: 6.75 },
    },
  },
  {
    id: 'dbs',
    name: 'DBS Bank India',
    shortName: 'DBS',
    type: 'Foreign Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.30, 15: 6.60, 18: 6.60, 24: 6.40, 36: 6.40, 60: 6.25 },
      senior:  { 7: 4.50, 12: 6.80, 15: 7.10, 18: 7.10, 24: 6.90, 36: 6.90, 60: 6.75 },
    },
  },
  {
    id: 'federal',
    name: 'Federal Bank',
    shortName: 'Federal',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.25, 15: 6.40, 18: 6.40, 24: 6.40, 36: 6.75, 60: 6.40 },
      senior:  { 7: 5.00, 12: 6.75, 15: 6.90, 18: 6.90, 24: 6.90, 36: 7.25, 60: 6.90 },
    },
  },
  {
    id: 'bandhan',
    name: 'Bandhan Bank',
    shortName: 'Bandhan',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 7.00, 15: 7.00, 18: 7.00, 24: 7.20, 36: 7.00, 60: 5.85 },
      senior:  { 7: 5.25, 12: 7.50, 15: 7.50, 18: 7.50, 24: 7.70, 36: 7.50, 60: 6.60 },
    },
  },
  {
    id: 'sib',
    name: 'South Indian Bank',
    shortName: 'South Indian',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.00, 12: 7.30, 15: 7.40, 18: 7.40, 24: 7.25, 36: 7.00, 60: 6.75 },
      senior:  { 7: 4.50, 12: 7.80, 15: 7.90, 18: 7.90, 24: 7.75, 36: 7.50, 60: 7.25 },
    },
  },
  {
    id: 'karnataka',
    name: 'Karnataka Bank',
    shortName: 'Karnataka',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 7.25, 15: 7.35, 18: 7.35, 24: 7.25, 36: 7.10, 60: 6.80 },
      senior:  { 7: 5.00, 12: 7.75, 15: 7.85, 18: 7.85, 24: 7.75, 36: 7.60, 60: 7.30 },
    },
  },
  {
    id: 'cityunion',
    name: 'City Union Bank',
    shortName: 'City Union',
    type: 'Private Bank',
    rates: {
      regular: { 7: 4.50, 12: 7.25, 15: 7.25, 18: 7.25, 24: 7.00, 36: 7.00, 60: 6.75 },
      senior:  { 7: 5.00, 12: 7.75, 15: 7.75, 18: 7.75, 24: 7.50, 36: 7.50, 60: 7.25 },
    },
  },
  {
    id: 'ausfb',
    name: 'AU Small Finance Bank',
    shortName: 'AU SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 5.00, 12: 6.35, 15: 6.90, 18: 6.90, 24: 7.00, 36: 7.00, 60: 6.75 },
      senior:  { 7: 5.50, 12: 6.85, 15: 7.40, 18: 7.40, 24: 7.50, 36: 7.50, 60: 7.25 },
    },
  },
  {
    id: 'esaf',
    name: 'ESAF Small Finance Bank',
    shortName: 'ESAF SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.50, 12: 6.00, 15: 7.75, 18: 7.75, 24: 8.25, 36: 6.75, 60: 6.25 },
      senior:  { 7: 5.00, 12: 6.50, 15: 8.25, 18: 8.25, 24: 8.75, 36: 7.25, 60: 6.75 },
    },
  },
  {
    id: 'northeast',
    name: 'North East Small Finance Bank',
    shortName: 'NE SFB',
    type: 'Small Finance Bank',
    rates: {
      regular: { 7: 4.00, 12: 6.25, 15: 7.25, 18: 7.75, 24: 7.50, 36: 7.00, 60: 6.50 },
      senior:  { 7: 4.50, 12: 6.50, 15: 7.25, 18: 7.75, 24: 7.50, 36: 7.25, 60: 6.75 },
    },
  },
  {
    id: 'hsbc',
    name: 'HSBC India',
    shortName: 'HSBC',
    type: 'Foreign Bank',
    rates: {
      regular: { 7: 3.00, 12: 4.00, 15: 6.25, 18: 4.10, 24: 4.25, 36: 7.00, 60: 6.00 },
      senior:  { 7: 3.50, 12: 4.50, 15: 6.75, 18: 4.60, 24: 4.75, 36: 7.50, 60: 6.50 },
    },
  },
  {
    id: 'sc',
    name: 'Standard Chartered India',
    shortName: 'StanChart',
    type: 'Foreign Bank',
    rates: {
      regular: { 7: 3.50, 12: 6.60, 15: 6.50, 18: 6.50, 24: 6.50, 36: 6.50, 60: 6.25 },
      senior:  { 7: 4.00, 12: 7.10, 15: 7.00, 18: 7.00, 24: 7.00, 36: 7.00, 60: 6.75 },
    },
  },
  {
    id: 'deutsche',
    name: 'Deutsche Bank India',
    shortName: 'Deutsche',
    type: 'Foreign Bank',
    rates: {
      regular: { 7: 3.50, 12: 6.50, 15: 6.75, 18: 6.75, 24: 6.50, 36: 6.50, 60: 6.00 },
      senior:  { 7: 4.00, 12: 7.00, 15: 7.25, 18: 7.25, 24: 7.00, 36: 7.00, 60: 6.50 },
    },
  },
];

// ── Bank display order — well-known first ─────────────────────────────────────
const BANK_ORDER = [
  // Big public & private banks everyone knows
  'sbi', 'hdfc', 'icici', 'axis', 'kotak',
  'pnb', 'bob', 'canara', 'boi', 'union',
  'indusind', 'idfcfirst', 'yes', 'idbi',
  // Mid-tier PSU banks
  'indianbank', 'centralbank', 'iob', 'uco', 'bom', 'punjabandsind',
  // Private banks (moderately known)
  'federal', 'bandhan', 'rbl', 'dcb', 'sib', 'karnataka', 'cityunion',
  // Foreign banks
  'dbs', 'hsbc', 'sc', 'deutsche',
  // Small Finance Banks
  'ausfb', 'ujjivan', 'equitas', 'jana', 'suryoday', 'esaf', 'northeast',
  // NBFCs
  'bajaj', 'shriram', 'mahindra', 'pfc',
];

const TENURE_OPTIONS = [
  { months: 7,  label: '7 months' },
  { months: 12, label: '1 year' },
  { months: 15, label: '15 months' },
  { months: 18, label: '18 months' },
  { months: 24, label: '2 years' },
  { months: 36, label: '3 years' },
  { months: 60, label: '5 years' },
];

// ── Bank brand colors ─────────────────────────────────────────────────────────
const BANK_BRAND = {
  sbi:       { bg: '#22409A', text: '#fff', label: 'SBI' },
  hdfc:      { bg: '#004C8F', text: '#fff', label: 'HDFC' },
  icici:     { bg: '#F16521', text: '#fff', label: 'ICICI' },
  axis:      { bg: '#97144D', text: '#fff', label: 'AXIS' },
  kotak:     { bg: '#EF3B23', text: '#fff', label: 'KMB' },
  pnb:       { bg: '#003087', text: '#fff', label: 'PNB' },
  bob:       { bg: '#F15A24', text: '#fff', label: 'BoB' },
  canara:    { bg: '#007DC2', text: '#fff', label: 'CNR' },
  iob:       { bg: '#00529B', text: '#fff', label: 'IOB' },
  union:     { bg: '#E87722', text: '#fff', label: 'UBI' },
  idbi:      { bg: '#00AEEF', text: '#fff', label: 'IDBI' },
  indusind:  { bg: '#B81C22', text: '#fff', label: 'IIB' },
  idfcfirst: { bg: '#660032', text: '#fff', label: 'IDFC' },
  yes:       { bg: '#00539B', text: '#fff', label: 'YES' },
  rbl:       { bg: '#E31837', text: '#fff', label: 'RBL' },
  dcb:       { bg: '#005DAA', text: '#fff', label: 'DCB' },
  ujjivan:   { bg: '#E05206', text: '#fff', label: 'UJV' },
  equitas:   { bg: '#004B87', text: '#fff', label: 'EQT' },
  suryoday:  { bg: '#F7941D', text: '#fff', label: 'SRY' },
  jana:      { bg: '#0071BC', text: '#fff', label: 'JANA' },
  shriram:   { bg: '#E87722', text: '#fff', label: 'SHR' },
  bajaj:     { bg: '#006BBA', text: '#fff', label: 'BJF' },
  mahindra:  { bg: '#C8102E', text: '#fff', label: 'MFin' },
  pfc:       { bg: '#1B3F7A', text: '#fff', label: 'PFC' },
  boi:       { bg: '#003478', text: '#fff', label: 'BOI' },
  centralbank: { bg: '#C41230', text: '#fff', label: 'CBI' },
  indianbank:  { bg: '#004B87', text: '#fff', label: 'IB' },
  uco:         { bg: '#005BAA', text: '#fff', label: 'UCO' },
  bom:         { bg: '#F7941D', text: '#fff', label: 'BOM' },
  punjabandsind: { bg: '#00529B', text: '#fff', label: 'P&S' },
  dbs:         { bg: '#DA2128', text: '#fff', label: 'DBS' },
  federal:     { bg: '#1F4E9E', text: '#fff', label: 'FED' },
  bandhan:     { bg: '#E31837', text: '#fff', label: 'BDH' },
  sib:         { bg: '#005EB8', text: '#fff', label: 'SIB' },
  karnataka:   { bg: '#003087', text: '#fff', label: 'KBL' },
  cityunion:   { bg: '#C8102E', text: '#fff', label: 'CUB' },
  ausfb:       { bg: '#E4002B', text: '#fff', label: 'AU' },
  esaf:        { bg: '#7B2D8B', text: '#fff', label: 'ESAF' },
  northeast:   { bg: '#006633', text: '#fff', label: 'NESF' },
  hsbc:        { bg: '#DB0011', text: '#fff', label: 'HSBC' },
  sc:          { bg: '#006F51', text: '#fff', label: 'SC' },
  deutsche:    { bg: '#0018A8', text: '#fff', label: 'DB' },
};

const TDS_THRESHOLD = 40000; // ₹40,000 interest per year for regular; ₹50,000 for senior
const TDS_THRESHOLD_SENIOR = 50000;
const TDS_RATE = 0.10; // 10%

// ── Calculation helpers ────────────────────────────────────────────────────────
function calcFD({ principal, months, annualRate, compounding, isSenior }) {
  if (!principal || !annualRate) return { maturity: 0, interest: 0, tds: 0, netMaturity: 0 };

  const years = months / 12;
  let maturity;

  if (compounding === 'simple') {
    maturity = principal * (1 + (annualRate / 100) * years);
  } else {
    // Quarterly compounding
    const n = 4;
    maturity = principal * Math.pow(1 + annualRate / (100 * n), n * years);
  }

  const interest = maturity - principal;
  const threshold = isSenior ? TDS_THRESHOLD_SENIOR : TDS_THRESHOLD;
  const annualInterest = interest / years;
  const tds = annualInterest > threshold ? interest * TDS_RATE : 0;
  const netMaturity = maturity - tds;

  return {
    maturity: Math.round(maturity),
    interest: Math.round(interest),
    tds: Math.round(tds),
    netMaturity: Math.round(netMaturity),
  };
}

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const fmtNum = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);

// ── Bank Logo — tries /images/banks/{id}.png first, falls back to brand badge ──
const BankLogo = ({ bank, size = 36 }) => {
  const [imgError, setImgError] = useState(false);
  const brand = BANK_BRAND[bank.id] || { bg: '#374151', text: '#fff', label: bank.shortName.slice(0, 3).toUpperCase() };
  const r = Math.floor(size * 0.22);

  if (!imgError) {
    return (
      <img
        src={`/images/banks/${bank.id}.png`}
        alt={bank.shortName}
        onError={() => setImgError(true)}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: r, flexShrink: 0, backgroundColor: '#f9fafb', padding: '3px' }}
      />
    );
  }

  const fontSize = brand.label.length > 3 ? Math.floor(size * 0.26) : Math.floor(size * 0.3);
  return (
    <div style={{
      width: size, height: size, borderRadius: r, background: brand.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, letterSpacing: '-0.3px',
      fontSize, fontWeight: 800, color: brand.text,
      fontFamily: 'Onest, sans-serif', userSelect: 'none',
    }}>
      {brand.label}
    </div>
  );
};

// ── Bank Selector Dropdown ─────────────────────────────────────────────────────
const BankSelector = ({ value, onChange, excludeId, label, color, isSenior, isOpen, onToggle }) => {
  const [search, setSearch] = useState('');
  const open = isOpen;

  const filtered = FD_BANKS
    .filter(b => b.id !== excludeId && b.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const ai = BANK_ORDER.indexOf(a.id);
      const bi = BANK_ORDER.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  const selected = FD_BANKS.find(b => b.id === value);

  const getRate1yr = (bank) => {
    const rateMap = isSenior ? bank.rates.senior : bank.rates.regular;
    return rateMap[12] || 0;
  };

  const RatePill = ({ rate }) => rate ? (
    <div style={{
      flexShrink: 0, padding: '3px 8px', borderRadius: '20px',
      backgroundColor: color + '15', border: `1px solid ${color}30`,
      fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 700, color,
    }}>
      {rate}% p.a.
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '6px' }}>
        {label}
      </label>
      <button
        onClick={() => onToggle()}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
          backgroundColor: '#f9fafb', border: `2px solid ${open ? color : '#e5e7eb'}`,
          transition: 'border-color 0.15s ease', gap: '8px',
        }}
      >
        {selected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <BankLogo bank={selected} size={44} color={color} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '14px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected.name}
              </div>
              <div style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', fontWeight: 500, marginTop: '1px' }}>
                {selected.type}
              </div>
            </div>
          </div>
        ) : (
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#9ca3af' }}>Select a bank...</span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {selected && <RatePill rate={getRate1yr(selected)} />}
          <ChevronDown size={16} color="#9ca3af" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
        </div>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 100,
          backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          border: '1px solid #f3f4f6', overflow: 'hidden',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={14} color="#9ca3af" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search banks..."
              style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#374151', background: 'transparent' }}
            />
          </div>
          {/* List */}
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#9ca3af' }}>No banks found</div>
            ) : filtered.map(bank => {
              const rate = getRate1yr(bank);
              return (
                <button
                  key={bank.id}
                  onClick={() => { onChange(bank.id); onToggle(false); setSearch(''); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', border: 'none', cursor: 'pointer', textAlign: 'left',
                    backgroundColor: bank.id === value ? '#f0fdf4' : 'transparent',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={e => { if (bank.id !== value) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={e => { if (bank.id !== value) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <BankLogo bank={bank} size={40} color={color} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '13px', color: '#111827' }}>{bank.name}</div>
                    <div style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', fontWeight: 500 }}>{bank.type}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    {rate > 0 && (
                      <div style={{
                        padding: '2px 8px', borderRadius: '20px',
                        backgroundColor: color + '12', border: `1px solid ${color}25`,
                        fontFamily: 'Onest, sans-serif', fontSize: '11px', fontWeight: 700, color,
                      }}>
                        {rate}%
                      </div>
                    )}
                    {bank.id === value && (
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Check size={10} color="#fff" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const FDCompareCalculator = () => {
  const [bank1Id, setBank1Id] = useState('sbi');
  const [bank2Id, setBank2Id] = useState('hdfc');
  const [amountStr, setAmountStr] = useState('100000');
  const [tenureMonths, setTenureMonths] = useState(12);
  const [isSenior, setIsSenior] = useState(false);
  const [compounding, setCompounding] = useState('quarterly');
  const [openDropdown, setOpenDropdown] = useState(null); // 'bank1' | 'bank2' | null
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const principal = parseInt(amountStr.replace(/[^0-9]/g, '')) || 0;

  const bank1 = FD_BANKS.find(b => b.id === bank1Id);
  const bank2 = FD_BANKS.find(b => b.id === bank2Id);

  const getRate = (bank, months) => {
    const rateMap = isSenior ? bank.rates.senior : bank.rates.regular;
    if (rateMap[months]) return rateMap[months];
    // Snap to nearest available tenure
    const keys = Object.keys(rateMap).map(Number).sort((a, b) => a - b);
    const nearest = keys.reduce((prev, cur) => Math.abs(cur - months) < Math.abs(prev - months) ? cur : prev);
    return rateMap[nearest] || 0;
  };

  const result1 = useMemo(() => calcFD({
    principal, months: tenureMonths,
    annualRate: getRate(bank1, tenureMonths),
    compounding, isSenior,
  }), [bank1, principal, tenureMonths, compounding, isSenior]);

  const result2 = useMemo(() => calcFD({
    principal, months: tenureMonths,
    annualRate: getRate(bank2, tenureMonths),
    compounding, isSenior,
  }), [bank2, principal, tenureMonths, compounding, isSenior]);

  const diff = Math.abs(result1.netMaturity - result2.netMaturity);
  const isTie = diff === 0 && result1.netMaturity > 0;
  const winner = isTie ? null : result1.netMaturity > result2.netMaturity ? 'bank1' : 'bank2';

  const handleShare = (platform) => {
    const text = encodeURIComponent('Compare FD rates across Indian banks with Kapitalised: kapitalised.com/calculators/fd-compare');
    const maps = {
      whatsapp: `https://wa.me/?text=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText('https://kapitalised.com/calculators/fd-compare');
      setCopied(true); setTimeout(() => setCopied(false), 2000); return;
    }
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const css = `
    .fd-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .fd-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .fd-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; }
    .no-spin { -moz-appearance: textfield; }
    .fd-card { padding: 36px; }
    .fd-two-col { display: flex; gap: 24px; align-items: flex-start; }
    .fd-compare-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .fd-seo { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .fd-seo { padding: 32px 40px 64px 40px !important; }
      .fd-card { padding: 28px !important; }
      .hero-section { padding: 32px 40px 80px 40px !important; }
    }
    @media (max-width: 639px) {
      .fd-two-col { flex-direction: column !important; }
      .fd-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .fd-seo { padding: 32px 20px 48px 20px !important; }
      .fd-compare-cols { grid-template-columns: 1fr !important; }
    }
  `;

  const BANK1_COLOR = '#115e59';
  const BANK2_COLOR = '#7c3aed';

  const ResultCard = ({ bank, result, color, isWinner, isTie }) => {
    const rate = getRate(bank, tenureMonths);
    return (
      <div style={{
        borderRadius: '16px',
        border: `2px solid ${isWinner ? color : isTie ? '#9ca3af' : '#e5e7eb'}`,
        padding: '20px', backgroundColor: isWinner ? color + '06' : isTie ? '#f3f4f6' : '#f9fafb',
        position: 'relative', transition: 'all 0.2s ease',
      }}>
        {isWinner && (
          <div style={{
            position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: color, color: '#fff', fontFamily: 'Onest, sans-serif',
            fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px',
            whiteSpace: 'nowrap', letterSpacing: '0.5px',
          }}>
            ✦ BETTER RETURNS
          </div>
        )}
        {isTie && (
          <div style={{
            position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#6b7280', color: '#fff', fontFamily: 'Onest, sans-serif',
            fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px',
            whiteSpace: 'nowrap', letterSpacing: '0.5px',
          }}>
            = EQUAL RETURNS
          </div>
        )}

        {/* Bank name + rate */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BankLogo bank={bank} size={36} color={color} />
            <div>
              <div style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '15px', color: '#111827' }}>{bank.shortName}</div>
              <div style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{bank.type}</div>
            </div>
          </div>
          <div style={{
            backgroundColor: color + '15', padding: '6px 12px', borderRadius: '8px',
            fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '18px', color: color,
          }}>
            {rate ? `${rate}%` : 'N/A'}
          </div>
        </div>

        {/* Bar: principal vs interest */}
        {result.maturity > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ height: '8px', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
              <div style={{ width: `${(result.maturity > 0 ? principal / result.maturity : 0.6) * 100}%`, backgroundColor: color + '40' }} />
              <div style={{ flex: 1, backgroundColor: color }} />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
              <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: color + '40', display: 'inline-block' }} /> Principal
              </span>
              <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: color, display: 'inline-block' }} /> Interest
              </span>
            </div>
          </div>
        )}

        {/* Numbers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { label: 'Maturity Amount', value: result.maturity > 0 ? fmt(result.maturity) : '—', bold: true },
            { label: 'Interest Earned', value: result.interest > 0 ? fmt(result.interest) : '—' },
            { label: 'TDS Deducted', value: result.tds > 0 ? fmt(result.tds) : '₹0' },
            { label: 'Net Maturity', value: result.netMaturity > 0 ? fmt(result.netMaturity) : '—', highlight: true, color },
          ].map(({ label, value, bold, highlight, color: c }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280' }}>{label}</span>
              <span style={{
                fontFamily: 'Onest, sans-serif', fontSize: bold || highlight ? '15px' : '13px',
                fontWeight: bold || highlight ? 700 : 500,
                color: highlight ? c : bold ? '#111827' : '#374151',
              }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>

      <Navbar background="#1a4d3e" />

      {/* ── Hero ── */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>FD Compare</span>
        </div>

        {/* Title + share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: '0 0 10px 0', lineHeight: 1 }}>
              FD Compare
            </h1>
            <span style={{
              background: 'linear-gradient(180deg, #FFB643 0%, #FFB93F 100%)',
              border: '0.62px solid #CF902C',
              boxShadow: 'inset 0px 1px 3px rgba(255,255,255,0.58), inset 0px -1px 2px rgba(207,144,44,0.5), 0px 2px 5px rgba(208,144,44,0.14)',
              color: '#603800',
              fontFamily: 'Onest, sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              letterSpacing: '-0.02em',
              padding: '6px 16px',
              borderRadius: '6px',
            }}>Latest Rates 2026</span>
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}
            >
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter', label: 'X', bg: '#000', icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy', label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Overlay to close dropdowns on outside click */}
        {openDropdown && (
          <div
            onClick={() => setOpenDropdown(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          />
        )}

        {/* ── Two-column layout ── */}
        <div className="fd-two-col" style={{ alignItems: 'flex-start', minWidth: 0 }}>

          {/* LEFT — Calculator */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="fd-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* Bank selectors */}
              <div className="fd-compare-cols" style={{ marginBottom: '28px' }}>
                <BankSelector value={bank1Id} onChange={setBank1Id} excludeId={bank2Id} label="Select Bank" color={BANK1_COLOR} isSenior={isSenior} isOpen={openDropdown === 'bank1'} onToggle={(close) => setOpenDropdown(close === false ? null : openDropdown === 'bank1' ? null : 'bank1')} />
                <BankSelector value={bank2Id} onChange={setBank2Id} excludeId={bank1Id} label="Compare from Bank" color={BANK2_COLOR} isSenior={isSenior} isOpen={openDropdown === 'bank2'} onToggle={(close) => setOpenDropdown(close === false ? null : openDropdown === 'bank2' ? null : 'bank2')} />
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Deposit Amount</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' }}>₹</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="no-spin"
                      value={amountStr}
                      onChange={e => setAmountStr(e.target.value.replace(/[^0-9]/g, ''))}
                      style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', width: '90px', textAlign: 'right' }}
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="fd-slider"
                  min={1000} max={5000000} step={1000}
                  value={Math.min(Math.max(principal, 1000), 5000000)}
                  onChange={e => setAmountStr(e.target.value)}
                  style={{ background: sliderBg(Math.min(Math.max(principal, 1000), 5000000), 1000, 5000000) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' }}>₹1,000</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' }}>₹50,00,000</span>
                </div>
              </div>

              {/* Tenure — slider + text + quick buttons */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Tenure</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="no-spin"
                      value={tenureMonths}
                      onChange={e => {
                        const v = parseInt(e.target.value) || 1;
                        setTenureMonths(Math.min(Math.max(v, 1), 60));
                      }}
                      style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', width: '32px', textAlign: 'right' }}
                    />
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>mo</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="fd-slider"
                  min={1} max={60} step={1}
                  value={tenureMonths}
                  onChange={e => setTenureMonths(parseInt(e.target.value))}
                  style={{ background: sliderBg(tenureMonths, 1, 60) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' }}>1 month</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' }}>5 years</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[{ months: 12, label: '1 yr' }, { months: 18, label: '18 mo' }, { months: 24, label: '2 yrs' }, { months: 36, label: '3 yrs' }, { months: 60, label: '5 yrs' }].map(({ months, label }) => (
                    <button
                      key={months}
                      onClick={() => setTenureMonths(months)}
                      style={{
                        fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '13px',
                        padding: '7px 16px', borderRadius: '50px', cursor: 'pointer',
                        backgroundColor: tenureMonths === months ? '#115e59' : '#f3f4f6',
                        color: tenureMonths === months ? '#ffffff' : '#6b7280',
                        border: tenureMonths === months ? 'none' : '1.5px solid #e5e7eb',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Senior Citizen checkbox */}
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                  <div
                    onClick={() => setIsSenior(!isSenior)}
                    style={{
                      width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                      border: `2px solid ${isSenior ? '#115e59' : '#d1d5db'}`,
                      backgroundColor: isSenior ? '#115e59' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s ease', cursor: 'pointer',
                    }}
                  >
                    {isSenior && <Check size={12} color="#fff" strokeWidth={3} />}
                  </div>
                  <span
                    onClick={() => setIsSenior(!isSenior)}
                    style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151', cursor: 'pointer' }}
                  >
                    Senior Citizen <span style={{ color: '#9ca3af', fontWeight: 400 }}>(+0.25–0.50% extra rate)</span>
                  </span>
                </label>
              </div>

              {/* Results heading + divider */}
              <div style={{ marginTop: '36px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{
                  fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '13px',
                  color: '#6b7280', margin: 0, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  Side-by-Side Comparison
                </h3>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
              </div>

              {/* Results */}
              <div className="fd-compare-cols">
                <ResultCard bank={bank1} result={result1} color={BANK1_COLOR} isWinner={winner === 'bank1'} isTie={isTie} />
                <ResultCard bank={bank2} result={result2} color={BANK2_COLOR} isWinner={winner === 'bank2'} isTie={isTie} />
              </div>

              {/* Difference / Equal banner */}
              {principal > 0 && result1.maturity > 0 && result2.maturity > 0 && (
                isTie ? (
                  <div style={{
                    marginTop: '20px', padding: '14px 20px', borderRadius: '12px',
                    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <Shield size={18} color="#6b7280" />
                    <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#4b5563', margin: 0, fontWeight: 500 }}>
                      Both banks offer <strong>equal returns</strong> for this tenure. You can choose based on safety or convenience.
                    </p>
                  </div>
                ) : (
                  <div style={{
                    marginTop: '20px', padding: '14px 20px', borderRadius: '12px',
                    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <TrendingUp size={18} color="#16a34a" />
                    <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#15803d', margin: 0, fontWeight: 500 }}>
                      <strong>{winner === 'bank1' ? bank1.shortName : bank2.shortName}</strong> gives you{' '}
                      <strong>{fmt(diff)}</strong> more in net returns for this deposit.
                    </p>
                  </div>
                )
              )}

              {/* TDS note */}
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', color: '#9ca3af', marginTop: '16px', marginBottom: 0, lineHeight: 1.6 }}>
                * TDS at 10% applies if annual interest exceeds ₹{isSenior ? '50,000' : '40,000'} (threshold for {isSenior ? 'senior citizens' : 'regular depositors'}). Submit Form 15G/15H to avoid TDS if income is below taxable limit.
              </p>
            </div>
          </div>

          {/* RIGHT — Sidebar */}
          <SmartCalculatorsSidebar currentSlug="fd-compare" />
        </div>
      </div>

      {/* ── SEO Section ── */}
      <div className="fd-seo">

        {/* What is FD */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '36px', color: '#111827', marginBottom: '16px', marginTop: 0 }}>
            What is an FD Comparison Calculator?
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            An FD Comparison Calculator helps you compare Fixed Deposit returns from different banks and NBFCs side by side. Instead of visiting multiple bank websites, you can instantly see which institution offers the best maturity amount for your deposit amount and tenure.
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7 }}>
            The calculator factors in interest rate, compounding method, TDS deductions, and senior citizen benefits to give you a true net-in-hand comparison.
          </p>
        </div>

        {/* How it works */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '20px' }}>How to Use the FD Comparison Calculator?</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { s: '1', t: 'Select two banks or NBFCs you want to compare' },
              { s: '2', t: 'Enter your deposit amount' },
              { s: '3', t: 'Choose the tenure that suits your needs' },
              { s: '4', t: 'Toggle Senior Citizen or compounding type if needed' },
            ].map(item => (
              <div key={item.s} style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e5e7eb' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#115e59', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: '#fff', fontWeight: 600, fontSize: '14px' }}>{item.s}</span>
                </div>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#374151', fontWeight: 500 }}>{item.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Simple vs Compound formula */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>FD Maturity Formulas</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ backgroundColor: '#115e59', padding: '24px', borderRadius: '16px' }}>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 600, color: '#4ade80', marginBottom: '10px', marginTop: 0, letterSpacing: '0.5px' }}>SIMPLE INTEREST</p>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '17px', fontWeight: 700, color: '#fff', fontStyle: 'italic', marginBottom: '12px' }}>M = P × (1 + r × t)</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[['P', 'Principal amount'], ['r', 'Annual rate / 100'], ['t', 'Tenure in years']].map(([sym, desc]) => (
                  <div key={sym} style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '7px 12px', borderRadius: '6px', display: 'flex', gap: '8px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '14px' }}>{sym}</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>= {desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ backgroundColor: '#1e1b4b', padding: '24px', borderRadius: '16px' }}>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 600, color: '#a5b4fc', marginBottom: '10px', marginTop: 0, letterSpacing: '0.5px' }}>QUARTERLY COMPOUND</p>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '17px', fontWeight: 700, color: '#fff', fontStyle: 'italic', marginBottom: '12px' }}>M = P × (1 + r/4)^(4t)</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[['P', 'Principal amount'], ['r', 'Annual rate / 100'], ['t', 'Tenure in years']].map(([sym, desc]) => (
                  <div key={sym} style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '7px 12px', borderRadius: '6px', display: 'flex', gap: '8px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#a5b4fc', fontSize: '14px' }}>{sym}</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>= {desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TDS section */}
        <div style={{ marginBottom: '48px', backgroundColor: '#fff7ed', padding: '24px', borderRadius: '16px', border: '1px solid #fed7aa' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '18px', color: '#92400e', marginBottom: '12px', marginTop: 0 }}>TDS on FD Interest — What You Should Know</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, marginBottom: '12px' }}>
            Banks deduct TDS (Tax Deducted at Source) at 10% on FD interest if the total interest earned in a financial year exceeds ₹40,000 (₹50,000 for senior citizens). This is deducted before you receive the maturity amount.
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, margin: 0 }}>
            If your total income is below the taxable limit, you can submit <strong>Form 15G</strong> (or <strong>Form 15H</strong> for senior citizens) to your bank to avoid TDS deduction.
          </p>
        </div>

        {/* FAQs */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '24px' }}>Frequently Asked Questions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { q: 'Which bank offers the highest FD interest rate in India?', a: 'Small Finance Banks like Suryoday, Equitas, and Ujjivan typically offer the highest FD rates (8–9.6%) compared to large PSU and private banks (6.5–7.75%). However, deposits above ₹5 lakh in smaller banks carry higher risk as DICGC insurance only covers up to ₹5 lakh per depositor per bank.' },
              { q: 'Is it safe to invest in NBFCs for higher FD returns?', a: 'NBFC FDs like Bajaj Finance and Shriram Finance offer higher rates but are not covered by DICGC insurance. Look for AAA-rated NBFCs and consider spreading your deposits across multiple institutions to manage risk.' },
              { q: 'Does compounding frequency affect my FD returns?', a: 'Yes. Quarterly compounding gives slightly higher returns than simple interest for the same rate. The difference becomes more significant for larger deposits and longer tenures. For a ₹1 lakh deposit at 7% for 3 years, quarterly compounding gives about ₹2,000 more than simple interest.' },
              { q: 'What is the benefit of comparing FDs before investing?', a: 'Even a 0.5% difference in interest rate on a ₹5 lakh deposit for 3 years translates to ~₹7,500 in additional returns. Over multiple FDs across a lifetime, this comparison can save or earn you lakhs of rupees.' },
              { q: 'Can I avoid TDS on my FD interest?', a: 'Yes. If your total annual income is below the basic exemption limit, you can submit Form 15G (below 60 years) or Form 15H (60 years and above) to your bank at the beginning of every financial year to prevent TDS deduction.' },
              { q: 'How are senior citizen FD rates different?', a: 'Most banks offer an additional 0.25% to 0.50% interest rate to senior citizens (60 years and above) on their FD deposits. Some banks like SBI and HDFC offer an extra 0.25% on top of that for super senior citizens (80 years and above).' },
            ].map(({ q, a }, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} style={{ borderRadius: '12px', border: `1px solid ${isOpen ? '#115e59' : '#e5e7eb'}`, overflow: 'hidden', transition: 'border-color 0.2s ease' }}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', backgroundColor: isOpen ? '#f0faf9' : '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px' }}
                  >
                    <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '15px', color: isOpen ? '#115e59' : '#1f2937' }}>{q}</span>
                    <span style={{ flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%', backgroundColor: isOpen ? '#115e59' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}>
                      <ChevronDown size={14} color={isOpen ? '#fff' : '#6b7280'} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 20px 18px 20px', backgroundColor: '#fff' }}>
                      <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
      {/* End SEO Section */}

    </div>
  );
};

export default FDCompareCalculator;
